import { TLocale } from '@internal/utils/machine/i18n/TLocale'
import { SystemPageKey } from 'page/SystemPageKey'
import { ParsedUrlQuery } from 'querystring'
import slugify from 'slugify'
import { PageTypeKey } from 'utils/page/PageTypeKey'
import {
  routingTable,
  TCompareMachinesRouteParams,
  TMachineCategoryPageRouteParams,
  TMachineDetailsRouteParams,
  TSearchResultsRouteParams,
} from '../routingTable'
import { getSlugifyOptions } from '../slugs/getSlugifyOptions'
import { TRoute } from '../TRoute'
import { queryParamsToString } from './queryParamsToString'
import { removeDoubleSlashes } from './removeDoubleSlashes'

type TParams =
  | {
      key: SystemPageKey.StartPage
    }
  | {
      key: SystemPageKey.SearchResults
      machineTypeName?: string
      queryParams?: TSearchResultsRouteParams['queryParams']
    }
  | ({
      key: SystemPageKey.PDP
    } & TMachineDetailsRouteParams)
  | {
      key: 'slug'
      slug: string
      queryParams?: ParsedUrlQuery
    }
  | ({
      key: SystemPageKey.CompareMachines
    } & TCompareMachinesRouteParams)
  | {
      key: PageTypeKey.MachineCategoryPage
      machineTypeName: string
      brandSlug?: string | null
      modelSlug?: string | null
      queryParams?: ParsedUrlQuery
    }

export const urlFor = (locale: TLocale, params: TParams | string) => {
  if (typeof params === 'string') {
    const slug = params
    return removeDoubleSlashes(`/${locale}/${slug}/`)
  } else if (params.key === 'slug') {
    const url = removeDoubleSlashes(`/${locale}/${params.slug}/`)
    const queryString =
      params.queryParams && queryParamsToString(params.queryParams)
    return `${url}${queryString && `?${queryString}`}`
  } else if (params.key === SystemPageKey.SearchResults) {
    const route = routingTable.systemPageRoutes[SystemPageKey.SearchResults][
      locale
    ] as TRoute<TSearchResultsRouteParams>

    const machineTypeSlug = params.machineTypeName
      ? slugify(params.machineTypeName, getSlugifyOptions(locale))
      : undefined
    return route.compile({
      pathParams: {
        machineTypeSlug: machineTypeSlug,
      },
      queryParams: params.queryParams ?? {},
    })
  } else if (params.key === PageTypeKey.MachineCategoryPage) {
    const route = routingTable.machineCategoryPageRoutes[
      locale
    ] as TRoute<TMachineCategoryPageRouteParams>

    const machineTypeSlug = slugify(
      params.machineTypeName,
      getSlugifyOptions(locale)
    )
    return route.compile({
      pathParams: {
        machineTypeSlug: machineTypeSlug,
        brandSlug: params.brandSlug ?? undefined,
        modelSlug: params.modelSlug ?? undefined,
      },
      queryParams: params.queryParams ?? {},
    })
  }

  return routingTable.systemPageRoutes[params.key][locale].compile(params)
}
